import { useQuery } from '@tanstack/react-query'
import { getMe, type MeResponse } from '@/lib/api'

const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'

/**
 * Current caller identity from GET /me.
 *
 * Drives tenant-aware UI: `is_admin` gates admin-only surfaces (certificates,
 * record create/delete) and `cloud_customer_id` is the caller's owning tenant,
 * used to prefill the DNS create form.
 *
 * In SKIP_AUTH (local dev) the backend treats the caller as admin, so we mirror
 * that here without needing a token.
 */
export function useMe() {
  const query = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const me = query.data
  return {
    me,
    // Default to admin while loading only under SKIP_AUTH; otherwise stay
    // locked-down (non-admin) until /me confirms, so we never briefly leak the
    // certificates surface to a customer.
    isAdmin: me?.is_admin ?? SKIP_AUTH,
    certAccess: me?.cert_access ?? SKIP_AUTH,
    cloudCustomerId: me?.cloud_customer_id ?? null,
    isLoading: query.isLoading,
  }
}
